import React, { lazy, Suspense, useEffect, useState } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { cookies } from "../axios";
import MyCustomLoader, {
  LoaderProvider,
} from "../components/loader/MyCustomLoader";
// import SampleForm from "../pages/admin/temple/Sample";
import ViewAllTemples from "../pages/user/viewalltemples/ViewAllTemples";
import FilterPopup from "../pages/user/viewalltemples/Filter";
import Chatbot from "../components/chatbot/Chatbot";
import CustomModal from "../components/reusablemodal/CustomModal";
import PrivacyPolicy from "../pages/privacypolicy/PrivacyPolicy";
import TermsAndConditions from "../pages/termsandconditions/TermsAndConditions";
import ContactUs from "../pages/contactus/ContactUs";
import ScrollToTop from "../components/scrolltotop/ScrollToTop";

// Auth pages
const Login = lazy(() => import("../pages/admin/login/login/login"));
const Register = lazy(() => import("../pages/admin/login/register/register"));
const ForgotPassword = lazy(() =>
  import("../pages/admin/login/forgetpassword/ForgotPassword")
);
const ResetPassword = lazy(() =>
  import("../pages/admin/login/resetpassword/ResetPassword")
);
const VerificationCode = lazy(() =>
  import("../pages/admin/login/verificationpassword/verificationCode")
);
const VerificationPassword = lazy(() =>
  import("../pages/admin/login/verificationpassword/verification")
);

// Layout
const Layout = lazy(() => import("../layout/Layout"));
const PageNotFound = lazy(() => import("../layout/PageNotFound"));

// App pages
// const HomeScreen = lazy(() => import("../pages/HomeScreen"));
const Dashboard = lazy(() => import("../pages/Dashboard"));
const Wishlist = lazy(() => import("../pages/admin/temple/Wishlist"));
const ViewTemple = lazy(() => import("../pages/admin/temple/ViewTemple"));
const TemplePhotos = lazy(() => import("../pages/admin/temple/TemplePhotos"));
const CreateTemple = lazy(() => import("../pages/admin/temple/CreateTemple"));
const TempleList = lazy(() => import("../pages/admin/temple/TempleList"));
const ApprovedTempleDetails = lazy(() =>
  import("../pages/admin/temple/ApprovedTempleDetails")
);
const WaitingForApprovalTempleDetails = lazy(() =>
  import("../pages/admin/temple/WaitingForApprovalTempleDetails")
);

// PROFILE
const Profile = lazy(() => import("../pages/admin/profile/Profile"));
const EditProfile = lazy(() => import("../pages/admin/profile/EditProfile"));
const ProfileList = lazy(() => import("../pages/admin/profile/ProfileList"));

// SUGGESTIONS
const SuggestUpdateBanner = lazy(() => import("../pages/user/Suggestion"));

// Protected Route Component
// const ProtectedRoute = ({ element }) => {
//   const userCookie = cookies.get("userDetails");
//   const parsedUser = userCookie || null;

//   if (!parsedUser?.user_id) {
//     alert("You don't have access, so please login to access this page.");
//     return <Navigate to="/login" replace />;
//   }
//   return element;
// };
const ProtectedRoute = ({ element }) => {
  const [showModal, setShowModal] = useState(false);
  const [redirect, setRedirect] = useState(false);

  const userCookie = cookies.get("userDetails");
  const parsedUser = userCookie || null;

  const handlePrimaryClick = () => {
    setShowModal(false);
    setRedirect(true); // trigger redirect after clicking OK
  };

  if (!parsedUser?.user_id) {
    if (!showModal) setShowModal(true); // open modal if user not logged in
  }

  if (redirect) {
    return <Navigate to="/login" replace />;
  }

  // if modal is shown, block page content
  if (showModal) {
    return (
      <CustomModal
        show={showModal}
        handleClose={handlePrimaryClick}
        primaryButtonLable={"OK"}
        title={null}
        primaryClick={handlePrimaryClick}
        // Hide secondary button and modal content
        secondaryButtonLable={null}
        renderModalContent={
          "This feature is available only for registered users. Please log in to create temples. You will be redirected to the login page — please log in or register to continue."
        }
      />
    );
  }

  return element;
};

function AppRoutes({ location }) {
  const userLocation = useLocation();

  useEffect(() => {
    if (userLocation.pathname === "/login") {
      cookies.remove("userDetails", { path: "/" });
      localStorage.removeItem("appLanguage");
    }
  }, [userLocation.pathname]);

  return (
    <Suspense>
      <ScrollToTop />
      <Routes>
        {/* Auth Pages without Header/Footer */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        {/* <Route
          path="/"
          element={
            <LoaderProvider>
              <HomeScreen location={location} />
            </LoaderProvider>
          }
        /> */}
        <Route path="/login" element={<Login />} />
        <Route path="/forgotPassword" element={<ForgotPassword />} />
        <Route path="/forgetPassword" element={<VerificationPassword />} />
        <Route path="/resetPassword" element={<ResetPassword />} />
        <Route path="/register" element={<Register />} />
        <Route path="/verificationcode" element={<VerificationCode />} />
        <Route path="/FilterPopup" element={<FilterPopup />} />

        {/* Layout Pages (with Header/Footer) */}
        <Route path="/" element={<Layout />}>
          {/* 🚀 Dashboard WITHOUT Loader */}
          <Route
            path="dashboard"
            element={
              <LoaderProvider>
                <Dashboard />
              </LoaderProvider>
            }
          />

          {/* <Route path="SampleForm" element={<SampleForm />} /> */}
          <Route
            path="ViewAllTemples"
            element={
              <LoaderProvider>
                <ViewAllTemples location={location} />
              </LoaderProvider>
            }
          />
          <Route
            path="favourites"
            element={
              <LoaderProvider>
                <ProtectedRoute element={<Wishlist />} />
              </LoaderProvider>
            }
          />
          <Route
            path="Chatbot"
            element={
              <LoaderProvider>
                <ProtectedRoute element={<Chatbot />} />
              </LoaderProvider>
            }
          />
          {/* 🚀 Other routes WITH Loader */}
          <Route
            path="view-temple/:templeId"
            element={
              <LoaderProvider>
                <ViewTemple />
              </LoaderProvider>
            }
          />
          <Route
            path="temple-photos"
            element={
              <LoaderProvider>
                <TemplePhotos />
              </LoaderProvider>
            }
          />
          <Route
            path="create-temple"
            element={
              <LoaderProvider>
                <ProtectedRoute element={<CreateTemple />} />
              </LoaderProvider>
            }
          />
          <Route
            path="temple-list"
            element={
              <LoaderProvider>
                <ProtectedRoute element={<TempleList />} />
              </LoaderProvider>
            }
          />
          <Route
            path="approved-temple-details"
            element={
              <LoaderProvider>
                <ProtectedRoute element={<ApprovedTempleDetails />} />
              </LoaderProvider>
            }
          />
          <Route
            path="waiting-approval-temple-details"
            element={
              <LoaderProvider>
                <ProtectedRoute element={<WaitingForApprovalTempleDetails />} />
              </LoaderProvider>
            }
          />
          {/* PROFILE */}
          <Route
            path="profile"
            element={
              <LoaderProvider>
                <ProtectedRoute element={<Profile />} />
              </LoaderProvider>
            }
          />
          <Route
            path="edit-profile"
            element={
              <LoaderProvider>
                <ProtectedRoute element={<EditProfile />} />
              </LoaderProvider>
            }
          />
          <Route
            path="profile-list"
            element={
              <LoaderProvider>
                <ProtectedRoute element={<ProfileList />} />
              </LoaderProvider>
            }
          />
          {/* SUGGESTIONS */}
          <Route
            path="suggestionBanner"
            element={
              <LoaderProvider>
                <ProtectedRoute element={<SuggestUpdateBanner />} />
              </LoaderProvider>
            }
          />

          {/* PUBLIC PAGES */}
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route
            path="/contact-us"
            element={
              <LoaderProvider>
                <ContactUs />
              </LoaderProvider>
            }
          />
          <Route
            path="/terms-and-conditions"
            element={<TermsAndConditions />}
          />
        </Route>

        {/* Fallback route */}
        <Route path="*" element={<PageNotFound />} />

        {/* DOWNLOAD PDF */}
        {/* <Route
          path="preview"
          element={
            <LoaderProvider>
              <PdfPreviewPage />
            </LoaderProvider>
          }
        /> */}
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
